import type { StatusTier } from "@/components/ui/badge"
import { Table, TableCard } from "@/components/ui/table"

import type { CohortRow } from "../types"

const status = (c: CohortRow): { label: string; tier: StatusTier } => {
  if (c.graduated) return { label: "Graduated", tier: "system" }
  if (c.ahead) return { label: "Booked", tier: "confirmed" }
  if (c.hidden_reason)
    return { label: c.hidden_reason.replace(/_/g, " "), tier: "system" }
  if (c.never_reached) return { label: "Never reached", tier: "caution" }
  if (c.to_continue) return { label: "To continue", tier: "neutral" }
  return { label: "—", tier: "neutral" }
}

/** One row per graduating class: what they have seen, what comes next. */
export function CohortsTable({ cohorts }: { cohorts: CohortRow[] }) {
  return (
    <TableCard.Root>
      <TableCard.Caption title="Cohorts" count={`${cohorts.length} cohorts`} />
      <Table aria-label="Cohorts" rows="two-line">
        <Table.Header>
          <Table.Head id="name" isRowHeader label="Cohort" className="w-full" />
          <Table.Head id="level" label="Now" />
          <Table.Head id="seen" label="Seen" />
          <Table.Head id="last" label="Last" />
          <Table.Head id="engagement" label="Engagement" align="right" />
          <Table.Head id="next" label="Next" />
          <Table.Head id="status" label="Status" />
        </Table.Header>
        <Table.Body items={cohorts}>
          {(c) => {
            const s = status(c)
            return (
              <Table.Row id={c.name}>
                <Table.Lead sub={c.derived ? "(derived)" : undefined}>
                  {c.name}
                </Table.Lead>
                <Table.Cell className="text-secondary">
                  {c.year_level_now ? `Year ${c.year_level_now}` : "—"}
                </Table.Cell>
                <Table.Cell>
                  {[...c.modules_core, ...c.modules_elective].join(" + ") ||
                    "—"}
                </Table.Cell>
                <Table.Id>{c.last_delivered}</Table.Id>
                <Table.Figure unit="%">
                  {c.engagement !== null ? Math.round(c.engagement) : null}
                </Table.Figure>
                <Table.Cell>{c.next_core ?? "—"}</Table.Cell>
                <Table.Status tier={s.tier}>{s.label}</Table.Status>
              </Table.Row>
            )
          }}
        </Table.Body>
      </Table>
    </TableCard.Root>
  )
}
